import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { apiFetch } from "../api/apiFetch";
import ErrorModal from "../components/ErrorModal";
import { usePermission } from "../hooks/usePermission";

export default function SupplierList() {
  const { hasPermission } = usePermission();
  const canCreate = hasPermission("suppliers.create");
  const canEdit = hasPermission("suppliers.edit");
  const canDelete = hasPermission("suppliers.delete");

  const [suppliers, setSuppliers] = useState([]);
  const [unauthorized, setUnauthorized] = useState(false);
  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [deletingSupplier, setDeletingSupplier] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    contact_person: "",
    email: "",
    phone: "",
    notes: ""
  });
  const [loading, setLoading] = useState(false);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);
  const [errorModal, setErrorModal] = useState({ show: false, message: "" });

  useEffect(() => {
    fetchSuppliers();
  }, [search, sortField, sortOrder]);

  const fetchSuppliers = () => {
    setLoadingSuppliers(true);
    const params = new URLSearchParams();
    if (search) params.append("search", search);
    params.append("sort", sortField);
    params.append("order", sortOrder);

    apiFetch(`/suppliers?${params.toString()}`)
      .then(setSuppliers)
      .catch((err) => {
        console.error("Fout bij ophalen leveranciers:", err);
        if (err.message.includes("403")) setUnauthorized(true);
      })
      .finally(() => setLoadingSuppliers(false));
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const handleAdd = () => {
    setEditingSupplier(null);
    setFormData({
      name: "",
      contact_person: "",
      email: "",
      phone: "",
      notes: ""
    });
    setShowModal(true);
  };

  const handleEdit = (supplier) => {
    setEditingSupplier(supplier);
    setFormData({
      name: supplier.name,
      contact_person: supplier.contact_person || "",
      email: supplier.email || "",
      phone: supplier.phone || "",
      notes: supplier.notes || ""
    });
    setShowModal(true);
  };

  const handleDelete = (supplier) => {
    setDeletingSupplier(supplier);
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    if (!deletingSupplier) return;

    setLoading(true);
    apiFetch(`/suppliers/${deletingSupplier.id}`, { method: "DELETE" })
      .then(() => {
        setShowDeleteModal(false);
        setDeletingSupplier(null);
        fetchSuppliers();
      })
      .catch((err) => {
        console.error("Fout bij verwijderen leverancier:", err);
        if (err.message.includes("400")) {
          setErrorModal({ show: true, message: err.message });
        } else {
          setErrorModal({ show: true, message: "Er is een fout opgetreden bij het verwijderen van de leverancier." });
        }
        if (err.message.includes("403")) setUnauthorized(true);
      })
      .finally(() => setLoading(false));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);

    const method = editingSupplier ? "PATCH" : "POST";
    const url = editingSupplier ? `/suppliers/${editingSupplier.id}` : "/suppliers";

    apiFetch(url, {
      method,
      body: JSON.stringify(formData),
    })
      .then(() => {
        setShowModal(false);
        setFormData({
          name: "",
          contact_person: "",
          email: "",
          phone: "",
          notes: ""
        });
        fetchSuppliers();
      })
      .catch((err) => {
        console.error("Fout bij opslaan leverancier:", err);
        setErrorModal({ show: true, message: "Er is een fout opgetreden bij het opslaan van de leverancier." });
        if (err.message.includes("403")) setUnauthorized(true);
      })
      .finally(() => setLoading(false));
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return "↕";
    return sortOrder === "asc" ? "↑" : "↓";
  };

  if (unauthorized) return <Navigate to="/unauthorized" replace />;

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Leveranciers</h1>
        {canCreate && (
          <button
            onClick={handleAdd}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            + Nieuwe Leverancier
          </button>
        )}
      </div>

      {/* Search Bar */}
      <div className="mb-4 flex gap-2">
        <input
          type="text"
          placeholder="Zoek op naam..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full max-w-md px-4 py-2 border rounded"
        />
        {search && (
          <button
            onClick={() => setSearch("")}
            className="px-4 py-2 border rounded hover:bg-gray-100 text-gray-700"
          >
            Wissen
          </button>
        )}
      </div>

      {/* Loading Indicator */}
      {loadingSuppliers && (
        <div className="flex justify-center items-center py-12 bg-white shadow rounded-lg">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Leveranciers laden...</span>
        </div>
      )}

      {/* Suppliers Table */}
      {!loadingSuppliers && (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <table className="min-w-full">
            <thead className="bg-gray-100">
              <tr>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-200"
                onClick={() => handleSort("name")}
              >
                Naam {getSortIcon("name")}
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-200"
                onClick={() => handleSort("contact_person")}
              >
                Contactpersoon {getSortIcon("contact_person")}
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-200"
                onClick={() => handleSort("email")}
              >
                Email {getSortIcon("email")}
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-200"
                onClick={() => handleSort("phone")}
              >
                Telefoon {getSortIcon("phone")}
              </th>
              {(canEdit || canDelete) && (
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                  Acties
                </th>
              )}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {suppliers.length === 0 ? (
              <tr>
                <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                  Geen leveranciers gevonden
                </td>
              </tr>
            ) : (
              suppliers.map((supplier) => (
                <tr key={supplier.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">
                    {supplier.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                    {supplier.contact_person || "-"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                    {supplier.email || "-"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                    {supplier.phone || "-"}
                  </td>
                  {(canEdit || canDelete) && (
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      {canEdit && (
                        <button
                          onClick={() => handleEdit(supplier)}
                          className="text-blue-600 hover:text-blue-900 mr-4"
                        >
                          Bewerken
                        </button>
                      )}
                      {canDelete && (
                        <button
                          onClick={() => handleDelete(supplier)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Verwijderen
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">
              {editingSupplier ? "Leverancier Bewerken" : "Nieuwe Leverancier"}
            </h2>
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Naam *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Contactpersoon
                </label>
                <input
                  type="text"
                  value={formData.contact_person}
                  onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Telefoon
                </label>
                <input
                  type="text"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                />
              </div>
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Opmerkingen
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                  rows="3"
                />
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border rounded hover:bg-gray-100"
                  disabled={loading}
                >
                  Annuleren
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400"
                  disabled={loading}
                >
                  {loading ? "Bezig..." : "Opslaan"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && deletingSupplier && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Leverancier Verwijderen</h2>
            <p className="mb-6 text-gray-700">
              Weet je zeker dat je de leverancier "<strong>{deletingSupplier.name}</strong>" wilt verwijderen?
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeletingSupplier(null);
                }}
                className="px-4 py-2 border rounded hover:bg-gray-100"
                disabled={loading}
              >
                Annuleren
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:bg-gray-400"
                disabled={loading}
              >
                {loading ? "Bezig..." : "Verwijderen"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      <ErrorModal
        show={errorModal.show}
        message={errorModal.message}
        onClose={() => setErrorModal({ show: false, message: "" })}
      />
    </div>
  );
}
